/**
 * Application Configuration
 *
 * This file reads environment variables from window.env (set by env-config.js)
 * and exports a frozen configuration object for use throughout the application.
 *
 * Generated by ov-config - do not edit manually.
 */

// Extend the Window interface to include our env object
declare global {
  // biome-ignore lint/style/useConsistentTypeDefinitions: override module
  interface Window {
    env: {
      AUTH0_DOMAIN: string;
      AUTH0_CLIENT_ID: string;

      BASE_PATH: string;
      ORG_BASE_PATH: string;
      ENV: string;
      GRAPHQL_CLIENT: string;

      ANALYTICS_API_KEY: string;
      ANALYTICS_AUTH_DOMAIN: string;
      ANALYTICS_PROJECT_ID: string;
      ANALYTICS_STORAGE_BUCKET: string;
      ANALYTICS_MESSAGING_SENDER_ID: string;
      ANALYTICS_APP_ID: string;
      ANALYTICS_MEASUREMENT_ID: string;
      MIX_PANEL_PROJECT_TOKEN: string;

      DATADOG_ENV: string;
      DATADOG_LOG_CLIENT_TOKEN: string;
      DATADOG_RUM_APP_ID: string;
      DATADOG_RUM_CLIENT_TOKEN: string;
      DD_RUM_VERSION: string;

      FLINKS_URL: string;
      GOOGLE_PLACES_KEY: string;
      PERSONA_TEMPLATE_ID: string;
      EMBEDDED_WEB_APP_URL: string;
      TINYMCE_API_KEY: string;
      TINYMCE_LICENSE_KEY: string;
      FLAGSMITH_ENVIRONMENT_ID: string;
      GOOGLE_OAUTH_CLIENT_ID: string;
    };
  }
}

// Ensure window.env is available
if (typeof window === 'undefined' || !window.env) {
  throw new Error(
    'Environment configuration not loaded. Make sure env-config.js is loaded before this module.',
  );
}

// Create and freeze the configuration object
export const CONFIG = Object.freeze({
  auth: {
    domain: window.env.AUTH0_DOMAIN,
    clientId: window.env.AUTH0_CLIENT_ID,
  },
  app: {
    basePath: window.env.BASE_PATH,
    orgBasePath: window.env.ORG_BASE_PATH,
    env: window.env.ENV,
    graphqlClient: window.env.GRAPHQL_CLIENT,
  },
  analytics: {
    apiKey: window.env.ANALYTICS_API_KEY,
    authDomain: window.env.ANALYTICS_AUTH_DOMAIN,
    projectId: window.env.ANALYTICS_PROJECT_ID,
    storageBucket: window.env.ANALYTICS_STORAGE_BUCKET,
    messagingSenderId: window.env.ANALYTICS_MESSAGING_SENDER_ID,
    appId: window.env.ANALYTICS_APP_ID,
    measurementId: window.env.ANALYTICS_MEASUREMENT_ID,
    mixPanelProjectToken: window.env.MIX_PANEL_PROJECT_TOKEN,
  },
  datadog: {
    env: window.env.DATADOG_ENV,
    logClientToken: window.env.DATADOG_LOG_CLIENT_TOKEN,
    rumAppId: window.env.DATADOG_RUM_APP_ID,
    rumClientToken: window.env.DATADOG_RUM_CLIENT_TOKEN,
    ddRumVersion: window.env.DD_RUM_VERSION,
  },
  services: {
    flinksUrl: window.env.FLINKS_URL,
    googlePlacesKey: window.env.GOOGLE_PLACES_KEY,
    personaTemplateId: window.env.PERSONA_TEMPLATE_ID,
    embeddedWebAppUrl: window.env.EMBEDDED_WEB_APP_URL,
    flagsmithEnvironmentId: window.env.FLAGSMITH_ENVIRONMENT_ID,
    googleOAuthClientId: window.env.GOOGLE_OAUTH_CLIENT_ID,
  },
  tinymce: {
    apiKey: window.env.TINYMCE_API_KEY,
    licenseKey: window.env.TINYMCE_LICENSE_KEY,
  },
} as const);

// Export individual sections for convenience
export const { auth, app, analytics, datadog, services, tinymce } = CONFIG;

// Export types for TypeScript users
export type AppConfig = typeof CONFIG;
export type AuthConfig = typeof CONFIG.auth;
export type AppSettings = typeof CONFIG.app;
export type AnalyticsConfig = typeof CONFIG.analytics;
export type DatadogConfig = typeof CONFIG.datadog;
export type ServicesConfig = typeof CONFIG.services;
export type TinyMCEConfig = typeof CONFIG.tinymce;
